/// From https://reactjsexample.com/responsive-tabs-with-react-js/
/// (https://maslianok.github.io/react-responsive-tabs/)
/// Licence: MIT
/// Notes:
/// Barebones container without support for resizing, responsive collapse to accordion,
/// 'showmore', hidden tabs, etc etc
///
/// --- [ *** NB *** TODO manage state in app state with MobX ] ---
///

import classnames from 'classnames';
import React, { PureComponent } from 'react';

import { TabPanel } from '../../components/tab-panel/tab-panel';
import { Tab } from '../../components/tab/tab';

const styles = require('./tab-container.css');

export interface InkBarProps {
  left: number | 0;
  width: number | 0;
}

export const InkBar = (props: InkBarProps) => (
  <div className={classnames(styles['inkbar-wrapper'])}>
    <div className={classnames(styles.inkbar)} style={{ left: props.left, width: props.width }} />
  </div>
);

interface TabsData {
  tabsVisible: TabInfo[];
  panels: PanelInfo;
}

interface PanelInfo {
  content?: React.ReactNode;
  getContent?(): void;
  key?: any;
  classNames?: string;
}

interface ClassNameSelector {
  className?: string;
  tabIndex?: any;
  selected?: boolean;
  disabled?: boolean;
}

interface TabInfo {
  title: string;
  classNames?: string;
  panelClassNames?: string;
  /// TODO This should be a union: getContent or child nodes
  content?: React.ReactNode;
  getContent?(): void; //?(): void
  selected: boolean;
  disabled?: boolean;
  /// Might need this - key might just be set to index
  id?: any;
  tabIndex?: any;
  key: React.ReactText;
}

export interface TabContainerProps {
  // list of tabs
  items: TabInfo[];
  // selected tab key
  selectedTabKey?: React.ReactText;
  // materialUI-like rail under the selected tab
  showInkBar?: boolean;
  // onChange active tab callback
  onChange?(tabkey: React.ReactText): void | null;
  // classnames
  containerClass?: string;
  tabsWrapperClass?: string;
  tabClass?: string;
  panelClass?: string;
  /// ???
  renderButtons?: JSX.Element;
}

/// --- [ TODO Manage in application state and use MobX to control ] ---
interface TabContainerState {
  tabDimensions: Object;
  tabsTotalWidth: number;
  blockWidth: number;
  selectedTabKey?: React.ReactText;
  lastPropTabKey?: React.ReactText;
  focusedTabKey: React.ReactText | null;
}

const tabPrefix = 'tab-';
const panelPrefix = 'panel-';

export class TabContainer extends PureComponent<TabContainerProps> {
  containerDiv: HTMLDivElement | null = null; //React.RefObject<HTMLDivElement> = null;
  tabRefs: Object = {};
  // selectedTabKeyProp?: React.ReactText = this.props.selectedTabKey;
  /// Moved from render() - assumed only set on mount
  showInkBar: boolean = this.props.showInkBar === undefined ? true : this.props.showInkBar;
  /// --- [ TODO Manage in application state and use MobX to control ] ---
  state: TabContainerState = {
    tabDimensions: {},
    tabsTotalWidth: 0,
    blockWidth: 0,
    selectedTabKey: this.props.selectedTabKey,
    lastPropTabKey: this.props.selectedTabKey,
    focusedTabKey: null
  };

  componentDidMount() {
    this.setTabsDimensions();
  }

  componentDidUpdate = (prevProps: TabContainerProps) => {
    const { items } = this.props;
    const nextPropTabKey = this.props.selectedTabKey;

    if (items !== prevProps.items) {
      this.setTabsDimensions();
    }

    ///
    /// Tab changes can come from two directions:
    /// 1. From the tab(div) onClick method (which calls onChangeTab) - this sets the state
    /// 2. From the props passed in.
    /// We want the props to override the click - but ONLY if they are different. We can assume
    /// that props will only override if the onClick function is not available (ie. not rendered).
    /// Method:
    ///  1. CDM sets saved prop value
    ///     a. Prop change
    ///        if (new) props !== saved props, set state, update saved
    ///     b. onClick change
    ///        if state !== saved prop value, do nothing

    if (nextPropTabKey !== this.state.lastPropTabKey) {
      this.setState({
        ...this.state,
        selectedTabKey: nextPropTabKey,
        lastPropTabKey: nextPropTabKey
      });
    }

    // this.selectedTabKeyProp = currentTabKeyProp;
  };

  onChangeTab = (nextTabKey: React.ReactText) => {
    const { onChange } = this.props;
    // change active tab
    this.setState({
      ...this.state,
      selectedTabKey: nextTabKey
      // lastPropTabKey: nextTabKey
    });
    // and update

    if (onChange) {
      onChange(nextTabKey);
    }
  };

  onFocusTab = (focusedTabKey: React.ReactText) => () => this.setState({ ...this.state, focusedTabKey });

  onBlurTab = () => this.setState({ ...this.state, focusedTabKey: null });

  // /// FIXME should this be here at all?
  onKeyDown = (event: React.KeyboardEvent) => {
    const { focusedTabKey } = this.state;
    if (event.keyCode === 13 && focusedTabKey !== null) {
      this.setState({ ...this.state, selectedTabKey: focusedTabKey });
    }
  };

  setTabsDimensions = () => {
    if (!this.containerDiv) {
      // it shouldn't happens evern. Just paranoic check
      return;
    }

    // initial wrapper width calculation
    const blockWidth = this.containerDiv.offsetWidth;

    // calculate width and offset for each tab
    let tabsTotalWidth = 0;
    const tabDimensions = {};
    Object.keys(this.tabRefs).forEach(key => {
      if (this.tabRefs[key]) {
        const width = this.tabRefs[key].tab.offsetWidth;
        tabDimensions[key.replace(tabPrefix, '')] = {
          width,
          offset: tabsTotalWidth
        };
        tabsTotalWidth += width;
      }
    });

    this.setState({ ...this.state, tabDimensions, tabsTotalWidth, blockWidth });
  };

  getTabs = (): TabsData => {
    const { items } = this.props;
    const selectedTabKey = this.getSelectedTabKey();

    /// tabIndex is used for ARIA atributes
    let tabIndex = 0;

    return items.reduce(
      (result: TabsData, item: TabInfo, index: React.ReactText) => {
        const { id, title, classNames, panelClassNames, content, getContent, disabled } = item;
        /// React key - if id is not provided fall back to index
        const key = id || index;

        const selected = selectedTabKey === key;
        const payload = { tabIndex, selected, disabled, key };
        const tabPayload = {
          ...payload,
          title,
          classNames
        };

        let panelPayload;
        if (content || getContent) {
          panelPayload = {
            ...payload,
            content: (!getContent && content) || undefined,
            getContent: (!content && getContent) || undefined,
            classNames: panelClassNames
          };
        }

        tabIndex += 1;

        result.tabsVisible.push(tabPayload);

        if (content || getContent) {
          result.panels[key] = panelPayload;
        }

        return result;
      },
      { tabsVisible: [], panels: {} }
    );
  };

  getTabProps = (tab: TabInfo) => {
    const classNames = this.getClassNamesFor('tab', {
      selected: tab.selected,
      tabIndex: tab.tabIndex,
      disabled: tab.disabled,
      className: tab.classNames
    });
    return {
      /// (Set explicitly in render)
      // ref: e => this.tabRefs[tabPrefix + tab.key] = e,
      selected: tab.selected,
      children: <span data-tour={tab.title}>{tab.title}</span>,
      key: tabPrefix + tab.key,
      id: tabPrefix + tab.key,
      originalKey: tab.key,
      onClick: this.onChangeTab,
      onFocus: this.onFocusTab,
      onBlur: this.onBlurTab,
      panelId: panelPrefix + tab.key,
      disabled: tab.disabled,
      classNames
    };
  };

  getPanelProps = (panel: PanelInfo) => ({
    getContent: panel.getContent,
    children: panel.content,
    key: panelPrefix + panel.key,
    id: panelPrefix + panel.key,
    tabId: tabPrefix + panel.key,
    classNames: this.getClassNamesFor('panel', { className: panel.classNames })
  });

  getClassNamesFor = (type: string, selector: ClassNameSelector) => {
    const { tabClass, panelClass } = this.props;
    switch (type) {
      case 'tab':
        return classnames(styles.tab, selector.className, tabClass, {
          [styles.tab__first]: !selector.tabIndex,
          [styles.tab__selected]: selector.selected,
          [styles.tab__disabled]: selector.disabled
        });
      case 'panel':
        return classnames(styles.panel, selector.className, panelClass);
      default:
        return '';
    }
  };

  getSelectedTabKey = () => {
    const { items } = this.props;
    const { selectedTabKey } = this.state;

    if (typeof selectedTabKey === 'undefined') {
      if (!items[0]) {
        return undefined;
      }

      return items[0].key || 0;
    }

    return selectedTabKey;
  };

  render() {
    const { /*showInkBar,*/ containerClass, tabsWrapperClass } = this.props;
    const { tabsVisible, panels } = this.getTabs();
    const selectedTabKey = this.getSelectedTabKey();
    const selectedTabDimensions = (selectedTabKey !== undefined && this.state.tabDimensions[selectedTabKey]) || {};

    const containerClasses = classnames(styles.container, containerClass);
    const tabsClasses = classnames(styles.tabs, tabsWrapperClass /*, { accordion: isCollapsed }*/);

    return (
      <div className={containerClasses} onKeyDown={this.onKeyDown}>
        <div className={tabsClasses} ref={e => (this.containerDiv = e)}>
          {tabsVisible.map((tab: TabInfo) => {
            /// Set up a ref here to capture - essentially - the Tab's <div> offsetWidth property.
            /// This Tab ref is used to read the Tab.tab property, which is in turn another ref set up
            /// inside the Tab component to the Tab's own <div> element
            /// This allows us to manage both the Inkbar and resizing efficiently.
            /// Forward refs are hard to implement reliably for this
            return <Tab {...this.getTabProps(tab)} ref={e => (this.tabRefs[tab.key] = e)} />;

            // /// TODO this might only be required if collapsed
            // if (selectedTabKey === tab.key) {
            //     result.push(<TabPanel {...this.getPanelProps(panels[tab.key])} />);
            // }
          })}
          <div className={styles.buttonContainer}>{this.props.renderButtons}</div>
        </div>

        {this.showInkBar && (
          <InkBar left={selectedTabDimensions.offset || 0} width={selectedTabDimensions.width || 0} />
        )}

        {selectedTabKey !== undefined && panels[selectedTabKey] && (
          <TabPanel {...this.getPanelProps(panels[selectedTabKey])} />
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/ui/common/tab-container/tab-container.tsx